import React, { useId, useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';

import Star from '../img/star.svg';

import GoogleRatingImage from '../img/google-ratings-image.png';

import { fonts } from '../gatsby-theme-material-ui-top-layout/theme';

import ArrowHorizontal from '../img/arrow-horizontal.svg';

import {
  PROPY_SPACE_GREY_DARK,
  PROPY_SPACE_GREY_LIGHT,
  PROPY_BLACK,
  shuffleArray,
} from '../utils';

const ZoneContainer = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
`;

const ScrollTrack = styled.div`
  display: flex;
  width: fit-content;
  transform: translate3d(${props => props.direction === 'right' ? -props.$scrollOffset : props.$scrollOffset}px, 0, 0);
  transition: transform 0.1s linear;
  will-change: transform;
`;

const ScrollSection = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 40px;
  padding: 0 20px;
`;

const TestimonialBodyContainer = styled.div`
  margin-top: ${props => props.theme.spacing(2)};
  color: ${PROPY_SPACE_GREY_DARK};
  .MuiTypography-root {
      font-weight: 300;
  }
`

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: ${props => props.mobile ? '300px' : '1060px'};
  margin-left: ${props => props.mobile ? 'auto' : 'auto'};
  margin-right: ${props => props.mobile ? 'auto' : 'auto'};
  align-items: ${props => props.mobile ? 'center' : 'start'};
  margin-bottom: ${props => props.mobile ? props.theme.spacing(4) : props.theme.spacing(8)};
`

const TestimonialAuthorTypography = styled(Typography)`
  color: ${PROPY_BLACK};
`

const TestimonialPositionTypography = styled(Typography)`
color: ${PROPY_SPACE_GREY_LIGHT};
`

const SingleTestimonialContainer = styled.div`
  background-color: #FFFFFF;
  padding: ${props => props.theme.spacing(4)};
  border-radius: 8px;
  min-width: 400px;
  width: ${props => {
    // Base width is 400px
    const baseWidth = 400;
    
    // If text is longer than 160 characters, we start expanding
    if (props.bodyLength > 160) {
      // Calculate how much extra width we need
      // Every ~40 characters beyond 160 needs roughly 100px more width
      // to maintain 4 lines (these numbers can be adjusted based on your font)
      const extraChars = props.bodyLength - 160;
      const extraWidth = Math.ceil(extraChars / 40) * 100;
      
      return `${baseWidth + extraWidth}px`;
    }
    
    return `${baseWidth}px`;
  }};
  height: 280px;
  display: flex;
  
  transition: all 0.3s ease-in-out;
  ${props => {
    if(props.mode === 'google') {
      return `
        flex-direction: column;
        justify-content: center;
        align-items: center;
      `
    } else {
      return `
        flex-direction: column;
        justify-content: space-between;
        // &:hover {
        //   background-color: ${PROPY_SPACE_GREY_LIGHT};

        //   // Change testimonial body text color
        //   ${TestimonialBodyContainer} .MuiTypography-root {
        //     color: #FFFFFF;  // or whatever color you want on hover
        //     font-weight: 400;
        //   }

        //   // Change author name color
        //   > div:last-child .MuiTypography-root:first-child {
        //     color: #FFFFFF;  // or whatever color you want on hover
        //   }

        //   // Change position text color
        //   > div:last-child .MuiTypography-root:last-child {
        //     color: rgba(255, 255, 255, 0.5);
        //   }
        // }
      `;
    }
  }}
`

const StarBox = styled(Box)`
  height: 20px;
  width: 20px;
  
  & path {
    fill: #38A6FB;
    transition: fill 0.2s ease-in-out;
  }
  
  ${SingleTestimonialContainer}:hover & path {
    // fill: #FFFFFF;
  }
`;

// const HoverZone = styled.div`
//   position: absolute;
//   top: 50%;
//   transform: translateY(-50%);
//   height: 100px;
//   width: 100px;
//   z-index: 100;
//   ${props => props.side === 'left' ? 'left: 0;' : 'right: 0;'}
//   transition: all 0.3s ease;
//   background-color: rgba(56, 166, 251, 0.2); // Light blue base color
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   cursor: pointer;

//   &:hover {
//     background-color: rgba(56, 166, 251, 0.2); // Darker on hover
//   }

//   &::after {
//     content: '${props => props.side === 'left' ? '←' : '→'}';
//     font-size: 24px;
//     color: #38A6FB;
//     opacity: 0.7;
//     transition: transform 0.3s ease;
//   }

//   &:hover::after {
//     transform: scale(1.2);
//     opacity: 1;
//   }
// `;

const MobileSlideContainer = styled.div`
  background-color: #FFFFFF;
  box-shadow: 3.24px 2.59px 2.59px 0px rgba(0, 0, 0, 0.02);
  flex-direction: column;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 500px;
  width: calc(100% - 32px);
  margin-left: auto;
  margin-right: auto;
  border-radius: 6px;
  border: 1px solid rgba(240, 238, 238, 1);
  padding-top: ${props => props.theme.spacing(4)};
  padding-bottom: ${props => props.theme.spacing(4)};
`

const InnerSlideContainer = styled.div`
  flex-direction: column;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  overflow: hidden;
  padding-left: ${props => props.theme.spacing(2)};
  padding-right: ${props => props.theme.spacing(2)};
`

const Controls = styled.div`
  display: flex;
  justify-content: center;
  align-items: end;
  margin-top: ${props => props.theme.spacing(4)};
`

const testimonialRow1 = shuffleArray([
  {
    stars: 5,
    body: "Jessica Carter was wonderful! She was always there to answer my questions and was always on top of everything. She was a pleasure to work with!",
    author: "Justin Intorcia",
    position: "Seller, Sarasota, FL",
  },
  {
    stars: 5,
    body: "Propy Title Agency they are absolutely the best! 100%. I've been closely following Propy for the past two years, and my journey with them has been nothing short of enlightening. Their team members embody the culture and vision through the service they provide.",
    author: "Ivania Arrechavala",
    position: "Broker Associate at LoKation Real Estate",
  },
  {
    stars: 5,
    body: "That is so amazing. For 37 years I have lived in the country and waited days to get paid, it's like a miracle.",
    author: "Tamera Bourne",
    position: "Associate Broker at eXp Realty",
  },
  {
    stars: 5,
    body: "I just completed a real estate transaction with Propyand I couldn't have been more pleased with the whole process. They were highly professional and efficient from beginning to end. I would highly recommend Propy and would certainly use them again myself.",
    author: "Steven Wheeler",
    position: "Seller",
  },
  {
    stars: 5,
    body: "My wife and I are very pleased with how smooth our real estate transaction went all the way through closing with Propy Title and Escrow. From submitting the details online to meeting with the notary at our home to sign the paperwork and getting funded within a matter of a few hours, it was all a great experience and we highly recommend them for your title needs.",
    author: "Oscar Bidopia",
    position: "Seller",
  },
  {
    stars: 5,
    body: "Even though I live in Texas I was easily able to buy a house in Florida using Propy. Everything was completed easily, quickly, and securely online.",
    author: "Michael Griffin",
    position: "Buyer",
  },
  {
    stars: 5,
    body: "Propy is such a great platform and the team they have is fantastic. Myclients always have such a great experience!",
    author: "Michael Garren",
    position: "Agent",
  },
  {
    stars: 5,
    body: "Absolutely thrilled with the seamless, remote real estate purchase in Miami, facilitated by Propy title and blockchain technology. The process was smooth, efficient, and entirely hassle-free, even from abroad. Exceptional service, highly recommend!",
    author: "Efgenia Goncalves",
    position: "Buyer",
  },
  {
    stars: 5,
    body: "The Propy team is amazing! Great customer service and always on top ofcommunication with us as the agents, as well as with our buyers and sellers! Thanks so much for all your amazing work!",
    author: "Samantha Sloan",
    position: "Agent",
  }
]);

const testimonialsRow2 = shuffleArray([
  {
    stars: 5,
    body: "Another successful closing. Always a smooth transaction when working with Propy. The only title company I've worked with that will continue to work after 5PM and on the weekends. Definitely looking forward to more closings with Propy in the near future.",
    author: "Trevon Betty",
    position: "Real Estate Agent at Passkey Realty LLC",
  },
  {
    stars: 5,
    body: "Hey Propy team, Thanks so much for working with us on closer and making sure everything went smoothly. Much appreciated.",
    author: "Chris Yeh",
    position: "Home Seller",
  },
  {
    stars: 5,
    body: "The speed is amazing! Thanks so much, Jessica! Can't wait to do another deal together soon!",
    author: "Mary Beth Blackwell",
    position: "Real Estate Agent at Out Fast Realty & Investments",
  },
  {
    stars: 5,
    body: "Propy is the BEST!!! Always communicative, on  top of their game and just the best!",
    author: "Jessica Benevento",
    position: "Mortgage Loan Officer",
  },
  {
    stars: 5,
    body: "Everything went great, timely and with thorough communication.",
    author: "Zane Belden",
    position: "Agent",
  },
  {
    stars: 5,
    body: "Everyone involved in this entire experience has bent over backwards to work with us. We were constantly in contact with each other and they were super supportive throughout this whole process. I definitelyrecommend this company for your title needs. Thank you so much for all of your help and hard work.",
    author: "Jayden Divane",
    position: "Buyer",
  },
  {
    stars: 5,
    body: "What a joy and privilege to work with this organization!",
    author: "Francie Perez",
    position: "Seller",
  },
  {
    stars: 5,
    body: "All the people at Propy Title are extremely helpful and professional. Their number one goal is to make the transaction smooth and successful for both buyers and sellers. Kirsten Heslop and Shay Sandifer are excellent problem solvers. No matter the issue, they work with a team of lenders and realtors to resolve things quickly and effectively.",
    author: "Ken Harlow",
    position: "Senior Mortgage Consultant",
  }
]);

const combinedTestimonials = [
  ...testimonialRow1,
  ...testimonialsRow2
];

const animationVariants = {
  enter: {
    x: 50,
    opacity: 0
  },
  center: {
    x: 0,
    opacity: 1
  },
  exit: {
    x: -50,
    opacity: 0
  }
};

const TestimonialsEndlessScroll = ({ 
  isConsideredMobile,
}) => {

  const componentId = useId();

  const [scrollOffset, setScrollOffset] = useState(0);
  // const [isHovering, setIsHovering] = useState(null); // 'left' or 'right' or null
  const [activeIndex, setActiveIndex] = useState(0);

  const handleNextMobile = () => {
    if (activeIndex < combinedTestimonials.length) {
      setActiveIndex(prev => prev + 1);
    }
  };

  const handlePrevMobile = () => {
    if (activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
    }
  };

  const baseScrollOffset = useRef(0); // Store the scroll-based offset
  const hoverOffset = useRef(0);
  // const animationFrameId = useRef(null);
  const scrollTrackRef = useRef(null);
  const containerRef = useRef(null);
  const minScrollRef = useRef(0);  // Changed from maxScrollRef
  
  const calculateBoundaries = useCallback(() => {
    if (scrollTrackRef.current && containerRef.current) {
      const trackWidth = scrollTrackRef.current.scrollWidth;
      const containerWidth = containerRef.current.clientWidth;
      // The minimum scroll value (most negative) represents the rightmost position
      minScrollRef.current = -(trackWidth - containerWidth);  // Note the negative sign
    }
  }, []);

  // Add this effect to initialize boundaries
  useEffect(() => {
    calculateBoundaries();
    window.addEventListener('resize', calculateBoundaries);
    return () => window.removeEventListener('resize', calculateBoundaries);
  }, [calculateBoundaries]);

  // useEffect(() => {
  //   let lastTimestamp = 0;
  //   const scrollSpeed = 2;

  //   const animate = (timestamp) => {
  //     if (!lastTimestamp) lastTimestamp = timestamp;
  //     const delta = timestamp - lastTimestamp;

  //     let newHoverOffset = hoverOffset.current;

  //     if (isHovering === 'left') {
  //       newHoverOffset -= scrollSpeed * (delta / 16);
  //     } else if (isHovering === 'right') {
  //       newHoverOffset += scrollSpeed * (delta / 16);
  //     }

  //     // Check boundaries before applying new offset
  //     const combinedOffset = baseScrollOffset.current + newHoverOffset;
  //     // Prevent scrolling beyond the start (right) and end (left)
  //     if (combinedOffset <= 0 && combinedOffset >= minScrollRef.current) {
  //       hoverOffset.current = newHoverOffset;
  //       setScrollOffset(combinedOffset);
  //     }

  //     lastTimestamp = timestamp;
      
  //     if (isHovering) {
  //       animationFrameId.current = requestAnimationFrame(animate);
  //     }
  //   };

  //   if (isHovering) {
  //     animationFrameId.current = requestAnimationFrame(animate);
  //   }

  //   return () => {
  //     if (animationFrameId.current) {
  //       cancelAnimationFrame(animationFrameId.current);
  //       animationFrameId.current = null;
  //     }
  //   };
  // }, [isHovering]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const newBaseOffset = -scrollPosition * 0.25;
      
      const combinedOffset = newBaseOffset + hoverOffset.current;
      // Apply the same boundary logic here
      if (combinedOffset <= 0 && combinedOffset >= minScrollRef.current) {
        baseScrollOffset.current = newBaseOffset;
        setScrollOffset(combinedOffset);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // const handleMouseEnter = (side) => {
  //   setIsHovering(side);
  // };

  // const handleMouseLeave = () => {
  //   setIsHovering(null);
  //   // Cancel any ongoing animation
  //   if (animationFrameId.current) {
  //     cancelAnimationFrame(animationFrameId.current);
  //     animationFrameId.current = null;
  //   }
  // };
  
  return (
    <ZoneContainer ref={containerRef}>
      <TitleContainer mobile={isConsideredMobile}>
        <Typography sx={{ fontFamily: fonts.spaceGrotesk, textTransform: 'uppercase', color: PROPY_SPACE_GREY_LIGHT, letterSpacing: '0.12em', ...(isConsideredMobile && { fontSize: '14px', lineHeight: '14px' }) }}>TESTIMONIALS</Typography>
        <Typography variant="h4" sx={{ fontFamily: fonts.spaceGrotesk, color: PROPY_BLACK, letterSpacing: '-0.01em', ...(isConsideredMobile && { fontSize: '24px', lineHeight: '24px' }) }}>What our clients say</Typography>
      </TitleContainer>
      {!isConsideredMobile &&
        <div style={{position: "relative"}}>
          {/* <HoverZone 
            side="left"
            onMouseEnter={() => handleMouseEnter('right')}
            onMouseLeave={handleMouseLeave}
          />
          <HoverZone 
            side="right"
            onMouseEnter={() => handleMouseEnter('left')}
            onMouseLeave={handleMouseLeave}
          /> */}
          <ScrollTrack $scrollOffset={scrollOffset} ref={scrollTrackRef}>
            {[0].map((i) => (
              <ScrollSection
                key={`${componentId}-${i}`}
                initial={{ x: 0 }}
              >
                <SingleTestimonialContainer
                  bodyLength={0}
                  mode={'google'}
                >
                  <img src={GoogleRatingImage}
                    alt="Google Rating"
                    style={{
                      width: '268px',
                      height: '72px'
                    }}
                  />
                </SingleTestimonialContainer>
                {testimonialRow1.map((entry, index) => (
                  <SingleTestimonialContainer
                    key={`${componentId}-${i}-${index}`}
                    bodyLength={entry.body.length}
                  >
                    <div>
                      <div className="flex">
                        {Array.from({length: entry.stars}).map((item, index) => 
                          <StarBox
                            key={`${componentId}-star-${i}-${index}`}
                            component={Star}
                          />
                        )}
                      </div>
                      <TestimonialBodyContainer>
                        <Typography style={{letterSpacing: '-0.04em', lineHeight: '18px', fontSize: '14px'}} sx={{ fontFamily: fonts.robotoMono }}>
                          “{entry.body}”
                        </Typography>
                      </TestimonialBodyContainer>
                    </div>
                    <div>
                      <TestimonialAuthorTypography style={{letterSpacing: '0.04em', fontWeight: 500, lineHeight: '15px', fontSize: '14px', textTransform: 'uppercase'}} sx={{ fontFamily: fonts.spaceGrotesk }}>
                        {entry.author}
                      </TestimonialAuthorTypography>
                      <TestimonialPositionTypography style={{letterSpacing: '0.04em', fontWeight: 400, lineHeight: '15px', fontSize: '14px',  textTransform: 'uppercase'}} sx={{ fontFamily: fonts.spaceGrotesk }}>
                        {entry.position}
                      </TestimonialPositionTypography>
                    </div>
                  </SingleTestimonialContainer>
                ))}
              </ScrollSection>
            ))}
          </ScrollTrack>
          <Box sx={{ height: '40px' }} />
          <ScrollTrack $scrollOffset={scrollOffset * 0.75} ref={scrollTrackRef}>
            {[0].map((i) => (
              <ScrollSection key={`${componentId}-row2-${i}`}>
                {testimonialsRow2.map((entry, index) => (
                  <SingleTestimonialContainer
                    key={`${componentId}-row2-${i}-${index}`}
                    bodyLength={entry.body.length}
                  >
                    <div>
                      <div className="flex">
                        {Array.from({length: entry.stars}).map((item, starIndex) => 
                          <StarBox
                            key={`desktop-${index}-star-${starIndex}`}
                            component={Star}
                          />
                        )}
                      </div>
                      <TestimonialBodyContainer>
                        <Typography style={{letterSpacing: '-0.04em', lineHeight: '18px', fontSize: '14px'}} sx={{ fontFamily: fonts.robotoMono }}>
                          "{entry.body}"
                        </Typography>
                      </TestimonialBodyContainer>
                    </div>
                    <div>
                      <TestimonialAuthorTypography style={{letterSpacing: '0.04em', fontWeight: 500, lineHeight: '15px', fontSize: '14px', textTransform: 'uppercase'}} sx={{ fontFamily: fonts.spaceGrotesk }}>
                        {entry.author}
                      </TestimonialAuthorTypography>
                      <TestimonialPositionTypography style={{letterSpacing: '0.04em', fontWeight: 400, lineHeight: '15px', fontSize: '14px',  textTransform: 'uppercase'}} sx={{ fontFamily: fonts.spaceGrotesk }}>
                        {entry.position}
                      </TestimonialPositionTypography>
                    </div>
                  </SingleTestimonialContainer>
                ))}
              </ScrollSection>
            ))}
          </ScrollTrack>
        </div>
      }
      {isConsideredMobile &&
        <>
          <MobileSlideContainer>
            <InnerSlideContainer>
              <AnimatePresence mode="wait">
                <motion.div
                  key={`featured-listings-mobile-view-${activeIndex}-step-number`}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  variants={animationVariants}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  style={{display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column'}}
                >
                  <div>
                    {Array.from({length: combinedTestimonials[activeIndex].stars}).map((item, starIndex) => 
                      <StarBox
                        key={`${activeIndex}-star-${starIndex}`}
                        component={Star}
                      />
                    )}
                    <TestimonialBodyContainer>
                      <Typography sx={{ marginBottom: '16px', fontFamily: fonts.robotoMono, letterSpacing: '-0.04em', lineHeight: '18px', fontSize: '14px' }}>
                        "{combinedTestimonials[activeIndex].body}"
                      </Typography>
                    </TestimonialBodyContainer>
                  </div>
                  <div>
                    <TestimonialAuthorTypography sx={{ marginBottom: '16px', fontFamily: fonts.spaceGrotesk, letterSpacing: '0.04em', fontWeight: 500, lineHeight: '15px', fontSize: '14px', textTransform: 'uppercase' }}>
                      {combinedTestimonials[activeIndex].author}
                    </TestimonialAuthorTypography>
                    <TestimonialPositionTypography sx={{ fontFamily: fonts.spaceGrotesk, letterSpacing: '0.04em', fontWeight: 400, lineHeight: '15px', fontSize: '14px',  textTransform: 'uppercase' }}>
                      {combinedTestimonials[activeIndex].position}
                    </TestimonialPositionTypography>
                  </div>
                </motion.div>
              </AnimatePresence>
            </InnerSlideContainer>
          </MobileSlideContainer>
          <Controls>
            <IconButton
              onClick={handlePrevMobile}
              disabled={activeIndex === 0}
              sx={{
                border: '1px solid #38A6FB',
                height: '44px',
                width: '44px',
                marginRight: '8px',
                opacity: activeIndex === 0 ? 0.5 : 1,
              }} 
              aria-label="backwards"
            >
              <Box
                component={ArrowHorizontal}
                sx={{
                  width: '24px',
                  height: '24px',
                  color: 'primary.main',
                  '& path': { fill: '#38A6FB' }
                }}
              />
            </IconButton>
            <IconButton
              onClick={handleNextMobile}
              disabled={activeIndex >= (combinedTestimonials.length - 1)}
              sx={{
                border: '1px solid #38A6FB',
                height: '44px',
                width: '44px',
                opacity: activeIndex >= (combinedTestimonials.length - 1) ? 0.5 : 1,
              }}
              aria-label="forwards"
            >
              <Box
                component={ArrowHorizontal}
                sx={{
                  width: 24,
                  height: 24,
                  transform: 'rotate(180deg)',
                  color: 'primary.main',
                  '& path': { fill: '#38A6FB' }
                }}
              />
            </IconButton>
          </Controls>
        </>
      }
    </ZoneContainer>
  );
};

export default TestimonialsEndlessScroll;